import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useAuth } from '../contexts/AuthContext';
import { 
    Heart, 
    Users, 
    MessageCircle, 
    Shield,
    Sparkles,
    Brain,
    Zap,
    Check,
    ChevronRight,
    ArrowRight,
    Pause,
    Volume2,
    BookOpen,
    Star,
    Compass,
    RotateCcw,
    HelpCircle,
    Loader2,
    X,
    Send,
    Mail
} from 'lucide-react';
import { CoRegulateLogo, CoRegulateMark, CoRegulateMarkAnimated } from '../components/Logo';
import { Menu } from 'lucide-react';
import { PrimaryButton, SecondaryButton, TertiaryButton } from '../components/brand/BrandButton';
import { BrandCard } from '../components/brand/BrandCard';
import { Heading, BodyText, SmallText } from '../components/brand/BrandTypography';
import { SafeIndicator, Badge } from '../components/brand/BrandIndicators';
import { IconContainer } from '../components/brand/BrandIcon';
import { AdminEditButton } from '../components/admin/AdminEditButton';
import { TestimonialsSection, DEFAULT_TESTIMONIALS } from '../components/Testimonials';
import { FoundingMemberBanner } from '../components/FoundingMember';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ═══════════════════════════════════════════════════════════════
// AI GUIDE CHAT WIDGET
// ═══════════════════════════════════════════════════════════════
const AIGuideChatWidget = ({ onNavigate }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [sessionId] = useState(() => `landing-${Date.now()}`);
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);

    // Scroll to bottom when messages change
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    // Focus input when chat opens
    useEffect(() => {
        if (isOpen && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isOpen]);

    const sendMessage = async (messageText) => {
        if (!messageText.trim() || isLoading) return;

        const userMessage = {
            role: 'user',
            content: messageText.trim()
        };

        setMessages(prev => [...prev, userMessage]);
        setInput('');
        setIsLoading(true);

        try {
            const response = await fetch(`${API_URL}/api/guide/chat`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    message: messageText,
                    session_id: sessionId,
                    has_completed_assessment: false
                })
            });

            if (response.ok) {
                const data = await response.json();
                
                setMessages(prev => [...prev, {
                    role: 'assistant',
                    content: data.response,
                    action: data.action
                }]);

                // Handle navigation actions
                if (data.action) {
                    const actionRoutes = {
                        'start_initial_assessment': '/initial-assessment',
                        'go_to_still_sphere': '/initial-assessment',
                        'go_to_calm_cycle': '/initial-assessment',
                        'go_to_micro_reset': '/initial-assessment',
                        'go_to_box_breathing': '/initial-assessment',
                        'go_to_navigator': '/initial-assessment',
                        'go_to_regulation_tools': '/initial-assessment',
                    };
                    
                    if (actionRoutes[data.action]) {
                        setTimeout(() => {
                            onNavigate(actionRoutes[data.action]);
                        }, 2000);
                    }
                }
            } else {
                setMessages(prev => [...prev, {
                    role: 'assistant',
                    content: "I'm here to help. Would you like to take a quick assessment to find the right tools for you?"
                }]);
            }
        } catch (error) {
            setMessages(prev => [...prev, {
                role: 'assistant',
                content: "Let's start fresh. Would you like to take a quick assessment?"
            }]);
        } finally {
            setIsLoading(false);
        }
    };

    const quickPrompts = [
        "I'm feeling overwhelmed",
        "My mind won't stop racing",
        "I don't know what I need"
    ];

    return (
        <>
            {/* Chat Button */}
            <button
                onClick={() => setIsOpen(!isOpen)}
                className={`fixed bottom-20 right-6 z-[60] w-14 h-14 rounded-full shadow-lg transition-all duration-300 flex items-center justify-center ${
                    isOpen 
                        ? 'bg-[#2A8585] rotate-0' 
                        : 'bg-[#1D6B6B] hover:bg-[#6A8A60] hover:scale-105'
                }`}
                data-testid="ai-chat-widget-btn"
            >
                {isOpen ? (
                    <X className="w-6 h-6 text-white" />
                ) : (
                    <MessageCircle className="w-6 h-6 text-white" />
                )}
            </button>

            {/* Chat Window */}
            {isOpen && (
                <div 
                    className="fixed bottom-36 right-6 z-[60] w-[360px] max-w-[calc(100vw-48px)] glass-card rounded-2xl shadow-soft-xl overflow-hidden"
                    style={{ maxHeight: 'calc(100vh - 180px)' }}
                >
                    {/* Header */}
                    <div className="bg-gradient-to-r from-[#1D6B6B] to-[#1D6B6B] px-4 py-3">
                        <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                                <Sparkles className="w-4 h-4 text-white" />
                            </div>
                            <div>
                                <SmallText className="font-medium text-white">CoRegulate Guide</SmallText>
                                <SmallText className="text-white/80 text-xs">Here to help you find what you need</SmallText>
                            </div>
                        </div>
                    </div>

                    {/* Messages */}
                    <div className="h-[320px] overflow-y-auto p-4 space-y-3 bg-white/50 backdrop-blur-sm">
                        {messages.length === 0 ? (
                            <div className="text-center py-4">
                                <BodyText className="text-[#7D8E95] mb-4">
                                    Hi! I can help you find the right tools for how you're feeling.
                                </BodyText>
                                <div className="flex flex-wrap gap-2 justify-center">
                                    {quickPrompts.map((prompt, idx) => (
                                        <button
                                            key={idx}
                                            onClick={() => sendMessage(prompt)}
                                            className="px-3 py-1.5 text-sm rounded-full bg-white/60 text-[#2A8585] hover:bg-[#7D8E95]/20 transition-colors"
                                        >
                                            {prompt}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            messages.map((msg, idx) => (
                                <div
                                    key={idx}
                                    className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                                >
                                    <div
                                        className={`max-w-[85%] rounded-2xl px-3 py-2 ${
                                            msg.role === 'user'
                                                ? 'bg-[#7D8E95]/20 text-[#1D6B6B]'
                                                : 'bg-white/60 text-[#1D6B6B]'
                                        }`}
                                    >
                                        <SmallText className="leading-relaxed whitespace-pre-wrap">
                                            {msg.content}
                                        </SmallText>
                                        {msg.action && (
                                            <div className="mt-2 flex items-center gap-1.5 text-[#1D6B6B]">
                                                <div className="w-3 h-3 border-2 border-[#1D6B6B] border-t-transparent rounded-full animate-spin" />
                                                <SmallText className="text-xs">Taking you there...</SmallText>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))
                        )}
                        
                        {isLoading && (
                            <div className="flex justify-start">
                                <div className="bg-white/60 rounded-2xl px-3 py-2">
                                    <div className="flex items-center gap-1">
                                        <div className="w-1.5 h-1.5 rounded-full bg-[#7D8E95] animate-pulse" />
                                        <div className="w-1.5 h-1.5 rounded-full bg-[#7D8E95] animate-pulse" style={{ animationDelay: '150ms' }} />
                                        <div className="w-1.5 h-1.5 rounded-full bg-[#7D8E95] animate-pulse" style={{ animationDelay: '300ms' }} />
                                    </div>
                                </div>
                            </div>
                        )}
                        
                        <div ref={messagesEndRef} />
                    </div>

                    {/* Input */}
                    <div className="p-3 border-t border-white/40 bg-white/70">
                        <div className="flex gap-2">
                            <input
                                ref={inputRef}
                                type="text"
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && sendMessage(input)}
                                placeholder="How are you feeling?"
                                className="flex-1 px-3 py-2 text-sm rounded-full bg-white border border-white/40 focus:outline-none focus:border-[#7D8E95] placeholder-[#7D8E95]"
                            />
                            <button
                                onClick={() => sendMessage(input)}
                                disabled={!input.trim() || isLoading}
                                className="w-9 h-9 rounded-full bg-[#1D6B6B] text-white flex items-center justify-center hover:bg-[#6A8A60] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                <Send className="w-4 h-4" />
                            </button>
                        </div>
                        <SmallText className="text-center text-[#7D8E95] text-xs mt-2">
                            Create an account to save your progress
                        </SmallText>
                    </div>
                </div>
            )}
        </>
    );
};

// Icon mapping for dynamic content
const ICON_MAP = {
    Brain, Zap, Users, Compass, HelpCircle, Pause, MessageCircle,
    Heart, Shield, Sparkles, Volume2, BookOpen, Star, RotateCcw
};

const Landing = () => {
    const navigate = useNavigate();
    const { isAuthenticated } = useAuth();
    const [pageContent, setPageContent] = useState(null);
    const [loading, setLoading] = useState(true);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    
    // Logo links to dashboard if authenticated, otherwise home
    const logoHref = isAuthenticated ? '/home' : '/';
    
    // Newsletter state
    const [newsletterEmail, setNewsletterEmail] = useState('');
    const [newsletterLoading, setNewsletterLoading] = useState(false);
    const [newsletterMessage, setNewsletterMessage] = useState('');
    const [newsletterSuccess, setNewsletterSuccess] = useState(false);

    // Newsletter handler
    const handleNewsletterSubmit = async (e) => {
        e.preventDefault();
        if (!newsletterEmail.trim() || newsletterLoading) return;
        
        setNewsletterLoading(true);
        setNewsletterMessage('');
        
        try {
            const response = await fetch(`${API_URL}/api/newsletter/subscribe`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                    email: newsletterEmail,
                    source: 'landing_page'
                })
            });
            
            const data = await response.json();
            
            if (response.ok) {
                setNewsletterSuccess(true);
                setNewsletterMessage(data.message);
                setNewsletterEmail('');
            } else {
                setNewsletterSuccess(false);
                setNewsletterMessage(data.detail || 'Something went wrong. Please try again.');
            }
        } catch (error) {
            setNewsletterSuccess(false);
            setNewsletterMessage('Unable to connect. Please try again later.');
        } finally {
            setNewsletterLoading(false);
        }
    };

    // Fetch dynamic content
    useEffect(() => {
        const fetchContent = async () => {
            try {
                const response = await fetch(`${API_URL}/api/pages/landing`);
                if (response.ok) {
                    const data = await response.json();
                    setPageContent(data);
                }
            } catch (error) {
                console.error('Error fetching landing content:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchContent();
    }, []);

    // Helper to get section by ID
    const getSection = (sectionId) => {
        return pageContent?.sections?.find(s => s.section_id === sectionId);
    };

    // Get sections by prefix (e.g., 'what_we_do_')
    const getSectionsByPrefix = (prefix) => {
        return pageContent?.sections
            ?.filter(s => s.section_id.startsWith(prefix) && s.is_visible !== false)
            .sort((a, b) => a.order - b.order) || [];
    };

    // ═══════════════════════════════════════════════════════════════
    // FALLBACK DATA (used while loading or if API fails)
    // ═══════════════════════════════════════════════════════════════
    
    const defaultWhatWeDoCards = [
        {
            icon: Heart,
            title: 'Navigate ADHD relationships',
            description: 'From conflict to repair — tools for breaking negative cycles, reconnecting after ruptures, and building stronger bonds.',
            variant: 'rose'
        },
        {
            icon: Brain,
            title: 'Regulate your nervous system',
            description: 'Micro-Resets, breathing exercises, and guided tools designed specifically for ADHD overwhelm and shutdown.',
            variant: 'sage'
        },
        {
            icon: HelpCircle,
            title: 'Understand your patterns',
            description: 'Gentle assessments that help you see what triggers you and your partner — without blame or judgment.',
            variant: 'lavender'
        },
        {
            icon: Users,
            title: 'Co-regulate together',
            description: 'Partner tools for those moments when you need to come back to each other after things get hard.',
            variant: 'blue'
        }
    ];

    const defaultHowItWorksSteps = [
        {
            step: 1,
            title: 'Take a gentle assessment',
            description: 'Answer a few soft questions about how you\'re feeling. No judgment, just understanding.',
            icon: HelpCircle
        },
        {
            step: 2,
            title: 'Get personalized guidance',
            description: 'Based on your needs, we\'ll recommend the right tools — whether you\'re overwhelmed, disconnected, or just exploring.',
            icon: Compass
        },
        {
            step: 3,
            title: 'Follow one step at a time',
            description: 'Each action is sensory-based, trauma-informed, and designed for ADHD nervous systems.',
            icon: Pause
        },
        {
            step: 4,
            title: 'Regulate alone or together',
            description: 'Solo tools for grounding yourself. Partner tools for reconnecting with someone you trust.',
            icon: Users
        }
    ];

    // Build dynamic data from API or use fallbacks
    const variants = ['sage', 'lavender', 'rose', 'blue'];
    const whatWeDoSections = getSectionsByPrefix('what_we_do_');
    const whatWeDoCards = whatWeDoSections.length > 0 
        ? whatWeDoSections.map((section, idx) => ({
            icon: section.icon ? ICON_MAP[section.icon] || Brain : defaultWhatWeDoCards[idx]?.icon || Brain,
            title: section.title,
            description: section.content,
            variant: variants[idx % variants.length]
        }))
        : defaultWhatWeDoCards;

    const howItWorksSections = getSectionsByPrefix('how_it_works_');
    const defaultIcons = [HelpCircle, Pause, Users, MessageCircle];
    const howItWorksSteps = howItWorksSections.length > 0
        ? howItWorksSections.map((section, idx) => ({
            step: idx + 1,
            title: section.title,
            description: section.content,
            icon: section.icon ? ICON_MAP[section.icon] || defaultIcons[idx] : defaultIcons[idx] || HelpCircle
        }))
        : defaultHowItWorksSteps;

    // Get hero content
    const heroTitle = pageContent?.title || 'CoRegulate';
    const heroTagline = pageContent?.subtitle || 'Made for ADHD hearts, shaped by ADHD minds.';
    const heroText = pageContent?.hero_text || 'Here to quiet the noise, nurture and repair connections, and guide you gently back to calm.';
    const ctaPrimary = pageContent?.cta_primary_text || 'Start regulating';
    const ctaSecondary = pageContent?.cta_secondary_text || 'How it works';

    // Get testimonials from section
    const testimonialSection = getSection('testimonial_1');
    const testimonials = testimonialSection 
        ? [testimonialSection.content, testimonialSection.quote].filter(Boolean)
        : [
            "The initial assessment helped me understand myself better.",
            "Finally, tools that actually work for my ADHD brain.",
            "I feel calmer within seconds."
        ];

    const challenges = [
        { text: 'Emotional overwhelm & shutdown', icon: Brain },
        { text: 'Relationship conflict & repair', icon: Heart },
        { text: 'Racing thoughts & spiralling', icon: Zap },
        { text: 'Feeling disconnected from your partner', icon: Users },
        { text: 'Not knowing what you need', icon: HelpCircle },
        { text: 'Breaking negative cycles', icon: RotateCcw },
        { text: 'Regulating after arguments', icon: MessageCircle },
        { text: 'Finding words when you can\'t speak', icon: Volume2 }
    ];

    // ═══════════════════════════════════════════════════════════════
    // RENDER
    // ═══════════════════════════════════════════════════════════════

    return (
        <div className="min-h-screen">
            {/* SEO Meta Tags - Schema is already in index.html */}
            <Helmet>
                <title>CoRegulate | ADHD Emotional Regulation App - Made for ADHD Hearts</title>
                <meta name="description" content="CoRegulate helps adults with ADHD manage emotional overwhelm with gentle, trauma-informed tools. Personalized assessments, breathing exercises, guided meditations, and relationship support." />
                <meta name="keywords" content="ADHD, emotional regulation, ADHD app, emotional dysregulation, ADHD tools, anxiety relief, ADHD relationships, breathing exercises, guided meditation" />
                <link rel="canonical" href="https://coregulateadhd.com/landing" />
                
                {/* Open Graph */}
                <meta property="og:title" content="CoRegulate | ADHD Emotional Regulation App" />
                <meta property="og:description" content="Gentle, trauma-informed tools for ADHD emotional regulation. Made for ADHD hearts, shaped by ADHD minds." />
                <meta property="og:type" content="website" />
                <meta property="og:url" content="https://coregulateadhd.com" />
                <meta property="og:image" content="https://coregulateadhd.com/og-image.png" />
                
                {/* Twitter Card */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="CoRegulate | ADHD Emotional Regulation App" />
                <meta name="twitter:description" content="Gentle, trauma-informed tools for ADHD emotional regulation." />
                <meta name="twitter:image" content="https://coregulateadhd.com/og-image.png" />
            </Helmet>
            
            {/* AI Guide Chat Widget */}
            <AIGuideChatWidget onNavigate={navigate} />
            
            {/* Admin Edit Button */}
            <AdminEditButton tab="pages" />
            
            {/* ════════════════════════════════════════════════════════
                NAVIGATION
            ════════════════════════════════════════════════════════ */}
            <nav className="fixed top-0 left-0 right-0 z-50 glass-header">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
                    <a href={logoHref} className="block" data-testid="header-logo">
                        <CoRegulateLogo size="md" />
                    </a>
                    
                    {/* Desktop Navigation */}
                    <div className="hidden md:flex items-center gap-3">
                        <a 
                            href="/how-it-works"
                            className="flex items-center px-3 py-2 text-sm font-medium text-[#7D8E95] hover:text-[#1D6B6B] transition-colors"
                            data-testid="nav-how-it-works-link"
                        >
                            How It Works
                        </a>
                        <a 
                            href="/tools"
                            className="flex items-center px-3 py-2 text-sm font-medium text-[#7D8E95] hover:text-[#1D6B6B] transition-colors"
                            data-testid="nav-tools-link"
                        >
                            Tools
                        </a>
                        <a 
                            href="/about"
                            className="flex items-center px-3 py-2 text-sm font-medium text-[#7D8E95] hover:text-[#1D6B6B] transition-colors"
                            data-testid="nav-about-link"
                        >
                            About
                        </a>
                        <a 
                            href="/pricing"
                            className="flex items-center px-3 py-2 text-sm font-medium text-[#1D6B6B] hover:text-[#145555] transition-colors"
                            data-testid="nav-pricing-link"
                        >
                            Pricing
                        </a>
                        {isAuthenticated ? (
                            <a 
                                href="/home"
                                className="inline-flex items-center justify-center px-5 py-2.5 text-sm font-medium rounded-full bg-[#1D6B6B] text-white hover:bg-[#145555] transition-all shadow-md"
                                data-testid="nav-dashboard-link"
                            >
                                Dashboard
                            </a>
                        ) : (
                            <>
                                <a 
                                    href="/auth?mode=login"
                                    className="flex items-center px-3 py-2 text-sm font-medium text-[#7D8E95] hover:text-[#1D6B6B] transition-colors"
                                    data-testid="nav-sign-in-link"
                                >
                                    Sign in
                                </a>
                                <a 
                                    href="/auth"
                                    className="inline-flex items-center justify-center px-5 py-2.5 text-sm font-medium rounded-full bg-[#1D6B6B] text-white hover:bg-[#145555] transition-all shadow-md"
                                    data-testid="nav-get-started-link"
                                >
                                    Get Started
                                </a>
                            </>
                        )}
                    </div>
                    
                    {/* Mobile Navigation */}
                    <div className="flex md:hidden items-center gap-1.5">
                        {isAuthenticated ? (
                            <a 
                                href="/home"
                                className="inline-flex items-center justify-center px-3 py-1.5 text-xs font-medium rounded-full bg-[#1D6B6B] text-white hover:bg-[#145555] transition-all whitespace-nowrap"
                                data-testid="nav-mobile-dashboard"
                            >
                                Dashboard
                            </a>
                        ) : (
                            <a 
                                href="/auth?mode=login"
                                className="inline-flex items-center justify-center px-2.5 py-1 text-xs font-medium rounded-full border-2 border-[#1D6B6B] text-[#1D6B6B] hover:bg-[#1D6B6B]/10 transition-all whitespace-nowrap"
                                data-testid="nav-mobile-sign-in"
                            >
                                Login
                            </a>
                        )}
                        <button
                            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                            className="p-2 rounded-lg text-[#1D6B6B] hover:bg-[#1D6B6B]/10 transition-colors"
                            aria-label="Toggle menu"
                            data-testid="nav-mobile-menu-toggle"
                        >
                            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                        </button>
                    </div>
                </div>
                
                {/* Mobile Menu Dropdown */}
                {mobileMenuOpen && (
                    <div className="md:hidden bg-white/95 backdrop-blur-md border-t border-[#E8E1D6]">
                        <div className="px-4 py-3 space-y-1">
                            <a 
                                href="/how-it-works"
                                className="block px-4 py-3 rounded-lg text-base font-medium text-[#3D5A5A] hover:bg-white/70 transition-colors"
                                onClick={() => setMobileMenuOpen(false)}
                            >
                                How It Works
                            </a>
                            <a 
                                href="/tools"
                                className="block px-4 py-3 rounded-lg text-base font-medium text-[#3D5A5A] hover:bg-white/70 transition-colors"
                                onClick={() => setMobileMenuOpen(false)}
                            >
                                Tools
                            </a>
                            <a 
                                href="/about"
                                className="block px-4 py-3 rounded-lg text-base font-medium text-[#3D5A5A] hover:bg-white/70 transition-colors"
                                onClick={() => setMobileMenuOpen(false)}
                            >
                                About
                            </a>
                            <a 
                                href="/pricing"
                                className="block px-4 py-3 rounded-lg text-base font-medium text-[#3D5A5A] hover:bg-white/70 transition-colors"
                                onClick={() => setMobileMenuOpen(false)}
                            >
                                Pricing
                            </a>
                            <div className="pt-3 border-t border-[#E8E1D6] mt-3">
                                {isAuthenticated ? (
                                    <a 
                                        href="/home"
                                        className="block w-full text-center px-4 py-3 rounded-full bg-[#1D6B6B] text-white font-medium hover:bg-[#145555] transition-all"
                                        onClick={() => setMobileMenuOpen(false)}
                                    >
                                        Go to Dashboard
                                    </a>
                                ) : (
                                    <a 
                                        href="/auth"
                                        className="block w-full text-center px-4 py-3 rounded-full text-white font-medium hover:opacity-90 transition-all"
                                        style={{
                                            background: 'linear-gradient(135deg, #7D8E95 0%, #2A8585 35%, #1D6B6B 65%, #FFBB98 100%)'
                                        }}
                                        onClick={() => setMobileMenuOpen(false)}
                                    >
                                        Get Started Free
                                    </a>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </nav>

            {/* Founding Member Banner - Positioned below fixed nav */}
            <div className="pt-20">
                <FoundingMemberBanner variant="subtle" />
            </div>

            {/* ════════════════════════════════════════════════════════
                HERO SECTION
            ════════════════════════════════════════════════════════ */}
            <section className="relative pt-12 pb-20 md:pt-20 md:pb-28 overflow-hidden">
                {/* Abstract soft shapes for extra depth */}
                <div 
                    className="absolute top-20 left-10 w-64 h-64 rounded-full opacity-20"
                    style={{ background: 'radial-gradient(circle, rgba(52,70,72,0.4) 0%, transparent 70%)' }}
                />
                <div 
                    className="absolute bottom-10 right-10 w-80 h-80 rounded-full opacity-15"
                    style={{ background: 'radial-gradient(circle, rgba(125,142,149,0.3) 0%, transparent 70%)' }}
                />
                {/* Additional subtle sage accent - top right */}
                <div 
                    className="absolute top-40 right-20 w-32 h-32 rounded-full opacity-10 hidden md:block"
                    style={{ background: 'radial-gradient(circle, rgba(125,142,149,0.5) 0%, transparent 70%)' }}
                />
                
                <div className="relative max-w-4xl mx-auto px-6 text-center">
                    {/* Logo mark - animated */}
                    <div className="mb-10 brand-fade-in flex justify-center">
                        <CoRegulateMarkAnimated size={100} />
                    </div>
                    
                    {/* Main headline */}
                    <Heading 
                        as="h1" 
                        size="text-4xl md:text-5xl lg:text-6xl" 
                        weight="medium" 
                        className="mb-6 brand-fade-in tracking-tight"
                        style={{ 
                            animationDelay: '100ms',
                            letterSpacing: '-0.02em'
                        }}
                    >
                        <span className="gradient-text-teal">Co</span>
                        <span className="gradient-text-peach">Regulate</span>
                    </Heading>
                    
                    {/* Tagline */}
                    <BodyText 
                        size="lg" 
                        className="max-w-2xl mx-auto mb-4 brand-fade-in text-lg sm:text-xl md:text-2xl text-[#1D6B6B] font-medium"
                        style={{ animationDelay: '200ms' }}
                    >
                        {heroTagline}
                    </BodyText>
                    
                    {/* Subheadline */}
                    <BodyText 
                        size="lg" 
                        className="max-w-2xl mx-auto mb-8 brand-fade-in leading-relaxed text-base md:text-lg text-[#7D8E95]"
                        style={{ animationDelay: '300ms' }}
                    >
                        {heroText}
                    </BodyText>
                    
                    {/* Credibility Line */}
                    <p 
                        className="text-sm text-[#7D8E95] mb-10 brand-fade-in"
                        style={{ animationDelay: '350ms' }}
                    >
                        Professionally created & designed by a Clinical Hypnotherapist
                    </p>
                    
                    {/* CTA Buttons */}
                    <div 
                        className="flex flex-col sm:flex-row gap-4 justify-center brand-fade-in"
                        style={{ animationDelay: '400ms' }}
                    >
                        <button 
                            onClick={() => navigate('/initial-assessment')}
                            data-testid="hero-start-btn"
                            className="group relative px-10 py-4 rounded-full font-medium text-white overflow-hidden transition-all duration-500 ease-out hover:scale-[1.02] hover:shadow-[0_8px_30px_rgba(52,70,72,0.35)] active:scale-[0.98]"
                            style={{
                                background: 'linear-gradient(135deg, #7D8E95 0%, #2A8585 35%, #1D6B6B 65%, #FFBB98 100%)',
                                boxShadow: '0 4px 20px rgba(52,70,72,0.25)'
                            }}
                        >
                            <span className="relative z-10 flex items-center justify-center gap-2">
                                <span>{ctaPrimary}</span>
                                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                            </span>
                            {/* Subtle shine effect */}
                            <div 
                                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                                style={{
                                    background: 'linear-gradient(135deg, rgba(255,255,255,0.15) 0%, transparent 50%, transparent 100%)'
                                }}
                            />
                        </button>
                        <button 
                            onClick={() => {
                                document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' });
                            }}
                            data-testid="hero-learn-btn"
                            className="group relative px-10 py-4 rounded-full font-medium overflow-hidden transition-all duration-500 ease-out hover:scale-[1.02] active:scale-[0.98]"
                            style={{
                                background: 'rgba(255,255,255,0.7)',
                                color: '#1D6B6B',
                                border: '1.5px solid rgba(125,142,149,0.3)',
                                boxShadow: '0 2px 12px rgba(52,70,72,0.08)'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.background = 'rgba(255,187,152,0.15)';
                                e.currentTarget.style.borderColor = 'rgba(255,187,152,0.5)';
                                e.currentTarget.style.boxShadow = '0 4px 20px rgba(52,70,72,0.12)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'rgba(255,255,255,0.7)';
                                e.currentTarget.style.borderColor = 'rgba(125,142,149,0.3)';
                                e.currentTarget.style.boxShadow = '0 2px 12px rgba(52,70,72,0.08)';
                            }}
                        >
                            <span className="relative z-10 flex items-center justify-center gap-2">
                                <span>{ctaSecondary}</span>
                                <ChevronRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 opacity-60" />
                            </span>
                        </button>
                    </div>
                </div>

                {/* Breathing animation keyframes */}
                <style>{`
                    @keyframes breathe {
                        0%, 100% { transform: scale(1); opacity: 0.9; }
                        50% { transform: scale(1.05); opacity: 1; }
                    }
                `}</style>
            </section>

            {/* ════════════════════════════════════════════════════════
                WHAT COREGULATE DOES
            ════════════════════════════════════════════════════════ */}
            <section className="py-20 md:py-28">
                <div className="max-w-5xl mx-auto px-6">
                    {/* Section header */}
                    <div className="text-center mb-16">
                        <Heading as="h2" size="text-2xl md:text-3xl" className="text-[#1D6B6B] font-medium mb-4">
                            What CoRegulate does
                        </Heading>
                        <BodyText className="text-[#7D8E95] text-lg">
                            Support when you need it most
                        </BodyText>
                    </div>
                    
                    {/* Feature cards */}
                    <div className="grid md:grid-cols-2 gap-6">
                        {whatWeDoCards.map((card, index) => {
                            const variantGlassClasses = {
                                sage: 'glass-card-sage',
                                lavender: 'glass-card-lavender',
                                rose: 'glass-card-rose',
                                blue: 'glass-card-blue',
                                gold: 'glass-card-gold'
                            };
                            const glassClass = variantGlassClasses[card.variant] || 'glass-card';
                            
                            return (
                                <div 
                                    key={card.title}
                                    className={`${glassClass} rounded-3xl p-8 brand-slide-up card-hover relative`}
                                    style={{ animationDelay: `${index * 80}ms` }}
                                >
                                    {card.comingSoon && (
                                        <Badge variant="gold" size="sm" className="absolute top-4 right-4">
                                            Coming soon
                                        </Badge>
                                    )}
                                    <IconContainer variant={card.variant} size="lg" useGradient={true} className="mb-5 shadow-md">
                                        <card.icon className="w-6 h-6" />
                                    </IconContainer>
                                    <Heading as="h3" size="text-lg" weight="medium" className="mb-3">
                                        {card.title}
                                    </Heading>
                                    <BodyText className="leading-relaxed">
                                        {card.description}
                                    </BodyText>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* ════════════════════════════════════════════════════════
                HOW IT WORKS
            ════════════════════════════════════════════════════════ */}
            <section id="how-it-works" className="py-20 md:py-28 bg-white/70">
                <div className="max-w-5xl mx-auto px-6">
                    {/* Section header */}
                    <div className="text-center mb-16">
                        <Heading as="h2" size="text-2xl md:text-3xl" className="text-[#1D6B6B] font-medium mb-4">
                            How it works
                        </Heading>
                        <BodyText className="text-[#7D8E95] text-lg">
                            Simple steps to calm
                        </BodyText>
                    </div>
                    
                    {/* Steps */}
                    <div className="max-w-3xl mx-auto">
                        {howItWorksSteps.map((item, index) => (
                            <div 
                                key={item.step}
                                className="flex gap-6 mb-10 last:mb-0 brand-slide-up"
                                style={{ animationDelay: `${index * 100}ms` }}
                            >
                                {/* Step number circle - with gradient */}
                                <div className="flex-shrink-0">
                                    <div 
                                        className="w-14 h-14 rounded-full flex items-center justify-center text-lg font-semibold text-white shadow-md"
                                        style={{
                                            background: 'linear-gradient(145deg, #2A8585 0%, #1D6B6B 50%, #145555 100%)',
                                            boxShadow: '0 4px 15px rgba(52,70,72,0.25)'
                                        }}
                                    >
                                        {item.step}
                                    </div>
                                    {/* Connecting line - with sage accent */}
                                    {index < howItWorksSteps.length - 1 && (
                                        <div 
                                            className="w-0.5 h-10 mx-auto mt-2"
                                            style={{
                                                background: 'linear-gradient(to bottom, #1D6B6B 0%, #7D8E95 50%, #FFBB98 100%)'
                                            }}
                                        />
                                    )}
                                </div>
                                
                                {/* Content */}
                                <div className="pt-3">
                                    <Heading as="h3" size="text-lg" weight="medium" className="mb-2">
                                        {item.title}
                                    </Heading>
                                    <BodyText className="leading-relaxed">
                                        {item.description}
                                    </BodyText>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ════════════════════════════════════════════════════════
                DESIGNED FOR ADHD HEARTS
            ════════════════════════════════════════════════════════ */}
            <section className="py-20 md:py-28">
                <div className="max-w-4xl mx-auto px-6">
                    <div className="glass-card-sage rounded-3xl p-10 md:p-16 text-center">
                        <div className="w-20 h-20 rounded-full glass-card flex items-center justify-center mx-auto mb-8 icon-glow-sage">
                            <Heart className="w-10 h-10 text-[#1D6B6B]" />
                        </div>
                        <Heading as="h2" size="text-2xl md:text-3xl" className="text-[#1D6B6B] font-medium mb-4">
                            Made for ADHD hearts
                        </Heading>
                        <BodyText className="text-lg text-[#7D8E95] max-w-2xl mx-auto">
                            Built for the moments when thinking is hard, words disappear, or everything feels too much — 
                            whether you're navigating it alone or trying to reconnect with someone you love.
                        </BodyText>
                    </div>
                </div>
            </section>

            {/* ════════════════════════════════════════════════════════
                CHALLENGES WE HELP WITH
            ════════════════════════════════════════════════════════ */}
            <section className="py-20 md:py-28 bg-white/70">
                <div className="max-w-4xl mx-auto px-6">
                    {/* Section header */}
                    <div className="text-center mb-16">
                        <Heading as="h2" size="text-2xl md:text-3xl" className="text-[#1D6B6B] font-medium mb-4">
                            CoRegulate can help with
                        </Heading>
                        <BodyText className="text-[#7D8E95] text-lg">
                            Whether you're struggling alone or together
                        </BodyText>
                    </div>
                    
                    {/* Challenges grid */}
                    <div className="grid sm:grid-cols-2 gap-4">
                        {challenges.map((challenge, index) => (
                            <div 
                                key={challenge.text}
                                className="glass-card rounded-2xl p-5 flex items-center gap-4 brand-slide-up card-hover"
                                style={{ animationDelay: `${index * 50}ms` }}
                            >
                                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 bg-[rgba(52,70,72,0.1)]">
                                    <challenge.icon className="w-5 h-5 text-[#1D6B6B]" />
                                </div>
                                <BodyText className="font-medium text-[#344648]">
                                    {challenge.text}
                                </BodyText>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ════════════════════════════════════════════════════════
                SOCIAL PROOF / TESTIMONIALS
            ════════════════════════════════════════════════════════ */}
            <TestimonialsSection 
                testimonials={DEFAULT_TESTIMONIALS}
                title="What people say"
                subtitle="Real experiences from our community"
                showFeatured={true}
                maxItems={6}
                layout="featured"
            />

            {/* ════════════════════════════════════════════════════════
                EMAIL CAPTURE / NEWSLETTER
            ════════════════════════════════════════════════════════ */}
            <section className="py-20 md:py-28 bg-gradient-to-br from-[#1D6B6B] to-[#145555]">
                <div className="max-w-3xl mx-auto px-6 text-center">
                    <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center mx-auto mb-6">
                        <Mail className="w-8 h-8 text-white" />
                    </div>
                    <Heading as="h2" size="text-2xl md:text-3xl" className="text-white font-medium mb-4">
                        Free ADHD Regulation Tips
                    </Heading>
                    <BodyText className="text-white/80 text-lg mb-8 max-w-xl mx-auto">
                        Join our community and get weekly tips for emotional regulation, 
                        grounding techniques, and relationship strategies designed for ADHD minds.
                    </BodyText>
                    
                    {/* Email Form */}
                    <form 
                        onSubmit={handleNewsletterSubmit}
                        className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
                    >
                        <input
                            type="email"
                            value={newsletterEmail}
                            onChange={(e) => setNewsletterEmail(e.target.value)}
                            placeholder="Enter your email"
                            required
                            className="flex-1 px-5 py-4 rounded-full bg-white/10 border border-white/20 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-white/30 focus:bg-white/15 transition-all"
                            data-testid="newsletter-email-input"
                        />
                        <button
                            type="submit"
                            disabled={newsletterLoading}
                            className="px-8 py-4 bg-white text-[#1D6B6B] font-semibold rounded-full hover:bg-white/60 hover:shadow-lg disabled:opacity-50 transition-all flex items-center justify-center gap-2"
                            data-testid="newsletter-submit-btn"
                        >
                            {newsletterLoading ? (
                                <div className="w-5 h-5 border-2 border-[#1D6B6B] border-t-transparent rounded-full animate-spin" />
                            ) : (
                                <>
                                    Subscribe
                                    <ArrowRight className="w-4 h-4" />
                                </>
                            )}
                        </button>
                    </form>
                    
                    {/* Success/Error Message */}
                    {newsletterMessage && (
                        <div className={`mt-4 p-3 rounded-lg text-sm ${
                            newsletterSuccess 
                                ? 'bg-white/10 text-white' 
                                : 'bg-red-500/20 text-red-100'
                        }`}>
                            {newsletterMessage}
                        </div>
                    )}
                    
                    <SmallText className="text-white/50 mt-6">
                        No spam, unsubscribe anytime. Join 100+ ADHD hearts.
                    </SmallText>
                </div>
            </section>

            {/* ════════════════════════════════════════════════════════
                FINAL CTA
            ════════════════════════════════════════════════════════ */}
            <section className="py-20 md:py-28">
                <div className="max-w-3xl mx-auto px-6 text-center">
                    <div className="glass-card-sage rounded-3xl p-12 md:p-16">
                        <Heading as="h2" size="text-2xl md:text-3xl" className="mb-4">
                            Ready to find your calm?
                        </Heading>
                        <BodyText size="lg" className="mb-10 max-w-md mx-auto leading-relaxed">
                            Start with a gentle assessment. We'll guide you to the right tools.
                        </BodyText>
                        <PrimaryButton 
                            onClick={() => navigate('/initial-assessment')}
                            size="xl"
                            className="px-12"
                            data-testid="final-cta-btn"
                        >
                            <span>Take the assessment</span>
                            <ChevronRight className="w-4 h-4 ml-2" />
                        </PrimaryButton>
                    </div>
                </div>
            </section>

            {/* ════════════════════════════════════════════════════════
                FOOTER
            ════════════════════════════════════════════════════════ */}
            <footer className="glass-header border-t-0">
                <div className="max-w-5xl mx-auto px-6 py-16">
                    <div className="grid md:grid-cols-4 gap-10 mb-12">
                        {/* Brand column */}
                        <div className="md:col-span-1">
                            <CoRegulateLogo size="md" className="mb-4" />
                            <BodyText className="max-w-sm leading-relaxed">
                                Made for ADHD hearts, shaped by ADHD minds.
                            </BodyText>
                        </div>
                        
                        {/* Product */}
                        <div>
                            <SmallText className="font-medium text-[#1D6B6B] mb-4">Product</SmallText>
                            <div className="space-y-3">
                                <button 
                                    onClick={() => navigate('/how-it-works')}
                                    className="block text-[#7D8E95] hover:text-[#1D6B6B] transition-colors text-sm"
                                >
                                    How It Works
                                </button>
                                <button 
                                    onClick={() => navigate('/tools')}
                                    className="block text-[#7D8E95] hover:text-[#1D6B6B] transition-colors text-sm"
                                >
                                    Tools
                                </button>
                                <button 
                                    onClick={() => navigate('/pricing')}
                                    className="block text-[#7D8E95] hover:text-[#1D6B6B] transition-colors text-sm"
                                >
                                    Pricing
                                </button>
                                <button 
                                    onClick={() => navigate('/auth')}
                                    className="block text-[#7D8E95] hover:text-[#1D6B6B] transition-colors text-sm"
                                >
                                    Get Started
                                </button>
                            </div>
                        </div>
                        
                        {/* Resources */}
                        <div>
                            <SmallText className="font-medium text-[#1D6B6B] mb-4">Resources</SmallText>
                            <div className="space-y-3">
                                <button 
                                    onClick={() => navigate('/blog')}
                                    className="block text-[#7D8E95] hover:text-[#1D6B6B] transition-colors text-sm"
                                >
                                    Blog
                                </button>
                                <button 
                                    onClick={() => navigate('/faq')}
                                    className="block text-[#7D8E95] hover:text-[#1D6B6B] transition-colors text-sm"
                                >
                                    FAQ
                                </button>
                                <button 
                                    onClick={() => navigate('/help')}
                                    className="block text-[#7D8E95] hover:text-[#1D6B6B] transition-colors text-sm"
                                >
                                    Support
                                </button>
                                <button 
                                    onClick={() => navigate('/crisis')}
                                    className="block text-[#7D8E95] hover:text-[#1D6B6B] transition-colors text-sm"
                                >
                                    Crisis Resources
                                </button>
                            </div>
                        </div>
                        
                        {/* Company */}
                        <div>
                            <SmallText className="font-medium text-[#1D6B6B] mb-4">Company</SmallText>
                            <div className="space-y-3">
                                <button 
                                    onClick={() => navigate('/about')}
                                    className="block text-[#7D8E95] hover:text-[#1D6B6B] transition-colors text-sm"
                                >
                                    About Us
                                </button>
                                <button 
                                    onClick={() => navigate('/impact')}
                                    className="block text-[#7D8E95] hover:text-[#1D6B6B] transition-colors text-sm"
                                >
                                    Our Impact
                                </button>
                                <button 
                                    onClick={() => navigate('/contact')}
                                    className="block text-[#7D8E95] hover:text-[#1D6B6B] transition-colors text-sm"
                                >
                                    Contact
                                </button>
                            </div>
                        </div>
                    </div>
                    
                    {/* Bottom bar */}
                    <div className="pt-8 border-t border-[#7D8E95]/20 flex flex-col md:flex-row items-center justify-between gap-4">
                        <SmallText className="text-[#7D8E95]">
                            © 2025 CoRegulate. Made for ADHD hearts, shaped by ADHD minds.
                        </SmallText>
                        <div className="flex items-center gap-6">
                            <button 
                                onClick={() => navigate('/privacy')}
                                className="text-xs text-[#7D8E95] hover:text-[#1D6B6B] transition-colors"
                            >
                                Privacy Policy
                            </button>
                            <button 
                                onClick={() => navigate('/terms')}
                                className="text-xs text-[#7D8E95] hover:text-[#1D6B6B] transition-colors"
                            >
                                Terms of Service
                            </button>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default Landing;
